import React, { useEffect, useMemo, useState } from 'react';
import { StatusPill, EmptyState } from '../../ui';
import { associationService } from '../../../services/associationService';

const CONFIDENCE = {
  core: { label: 'In the association', variant: 'approved' },
  references: { label: 'Names the association', variant: 'review' },
  related: { label: 'Shares the site', variant: 'review' },
  nearby: { label: 'Neighbour', variant: 'inactive' },
};

// Public-records parcels staged for one lead. Nothing reaches the association's
// property list until "Import" (LeadCard); unticking here keeps a parcel out.
export function ParcelReview({ leadId, onChanged }) {
  const [parcels, setParcels] = useState(null);
  const [error, setError] = useState(null);
  const [showNearby, setShowNearby] = useState(false);

  useEffect(() => {
    let cancelled = false;
    associationService.listParcels(leadId).then(
      (rows) => { if (!cancelled) setParcels(rows); },
      (e) => { if (!cancelled) setError(e.message); }
    );
    return () => { cancelled = true; };
  }, [leadId]);

  const visible = useMemo(
    () => (parcels || []).filter((p) => showNearby || p.confidence !== 'nearby'),
    [parcels, showNearby]
  );

  const toggle = async (parcel) => {
    const next = !parcel.selected;
    setParcels((rows) => rows.map((r) => (r.id === parcel.id ? { ...r, selected: next } : r)));
    try {
      await associationService.setParcelSelected(parcel.id, next);
      onChanged?.();
    } catch (e) {
      setError(e.message);
      setParcels((rows) => rows.map((r) => (r.id === parcel.id ? { ...r, selected: !next } : r)));
    }
  };

  if (error) return <p className="text-sm font-medium text-red-600">{error}</p>;
  if (!parcels) return <p className="text-sm font-medium text-slate-500">Loading parcels…</p>;
  if (parcels.length === 0) {
    return <EmptyState icon="home_work" title="No parcels staged" body="Run the public-records research to stage this association's homes." />;
  }

  const nearbyCount = parcels.filter((p) => p.confidence === 'nearby').length;
  const selectedCount = parcels.filter((p) => p.selected && !p.imported_property_id).length;

  // Another named condo or plat on the same building or footprint. Associations
  // sometimes merge, so offer the whole community in one click.
  const relatedGroups = Object.values(
    parcels.filter((p) => p.confidence === 'related').reduce((groups, p) => {
      const name = p.subdivision || 'Unnamed community';
      (groups[name] = groups[name] || { name, rows: [] }).rows.push(p);
      return groups;
    }, {})
  );

  const setGroup = async (rows, next) => {
    const ids = rows.filter((r) => !r.imported_property_id).map((r) => r.id);
    setParcels((all) => all.map((r) => (ids.includes(r.id) ? { ...r, selected: next } : r)));
    try {
      await associationService.setParcelsSelected(ids, next);
      onChanged?.();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
          {selectedCount} selected to import · {parcels.length} parcels found
        </p>
        {nearbyCount > 0 && (
          <button
            type="button"
            onClick={() => setShowNearby(!showNearby)}
            className="border border-secondary text-secondary px-4 py-1.5 rounded-full text-xs font-bold hover:bg-secondary hover:text-white transition-all"
          >
            {showNearby ? 'Hide' : 'Show'} {nearbyCount} neighbours
          </button>
        )}
      </div>
      {relatedGroups.map((group) => {
        const included = group.rows.every((r) => r.selected);
        return (
          <div key={group.name} className="bg-section-bg rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="related-community">
            <div className="min-w-0">
              <p className="text-sm font-bold text-primary-dark">{group.name}</p>
              <p className="text-sm font-medium text-slate-500">
                {group.rows.length} parcel{group.rows.length === 1 ? '' : 's'} on the same site. Associations sometimes merge:
                ask the board whether it is part of theirs.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setGroup(group.rows, !included)}
              className={included
                ? 'border border-secondary text-secondary px-4 py-1.5 rounded-full text-xs font-bold hover:bg-secondary hover:text-white transition-all'
                : 'bg-secondary text-white px-4 py-1.5 rounded-full text-xs font-bold hover:brightness-110 active:scale-95 transition-all'}
            >
              {included ? 'Leave it out' : `Include all ${group.rows.length}`}
            </button>
          </div>
        );
      })}
      <div className="overflow-x-auto max-h-[28rem] overflow-y-auto rounded-2xl border border-amber-100/40 bg-white">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-section-bg">
            <tr>
              {['', 'Address', 'Unit', 'Owner (public record)', 'Mailing', 'Match'].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-tighter text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((p) => (
              <tr key={p.id} className={p.selected ? '' : 'opacity-60'}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={p.selected}
                    disabled={!!p.imported_property_id}
                    onChange={() => toggle(p)}
                    aria-label={`Include ${p.situs_address || 'parcel'} ${p.unit_number || ''}`}
                    className="accent-secondary h-4 w-4"
                  />
                </td>
                <td className="px-3 py-2 font-medium text-primary-dark whitespace-nowrap">{p.situs_address || '—'}</td>
                <td className="px-3 py-2 text-slate-600">{p.unit_number || ''}</td>
                <td className="px-3 py-2 text-slate-700">{p.owner_name || '—'}</td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{[p.owner_mailing_city, p.owner_mailing_state].filter(Boolean).join(', ')}</td>
                <td className="px-3 py-2">
                  {p.imported_property_id
                    ? <StatusPill label="Imported" variant="published" />
                    : <StatusPill label={CONFIDENCE[p.confidence]?.label || p.confidence} variant={CONFIDENCE[p.confidence]?.variant} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ParcelReview;
